import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';

import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { NgxEchartsModule } from 'ngx-echarts';
import { KeycloakInitService } from './shared/auth-services/keycloak-init.service';
import { KeycloakService } from 'keycloak-angular';
import { authHeadersInterceptor } from './interceptors/auth-headers.interceptor';
import { savedLocalhostUrlInterceptor } from './interceptors/saved-localhost-url.interceptor';
import { bundledConfigInterceptor } from './interceptors/bundled-config.interceptor';
import { PlatformService } from '@shared-lib/shared-services/platform.service.service';
import { ENVIRONMENT_CONFIG } from '@shared-lib/shared-services/environment-config.token';
import { setSelfServiceApiOverrides } from '@shared-lib/self-service-visualization/shared/self-service-api.utils';
import { environment } from '../environments/environment';
import { selfServiceBaseUrl } from './self-service-base-url';

// Custom override: ChartRendererService uses custom bar-chart (pink) and pie-chart (70% radius)
import {
  ChartRendererService,
  CustomGlobalFilterService,
  CustomChartFilterHandlerService,
  CustomConfigService,
} from '@custom';
import { ChartRendererService as SharedChartRendererService } from '@shared-lib/shared-services/chart-renderer.service';
import { GlobalFilterService as SharedGlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartFilterHandlerService as SharedChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { ConfigService as SharedConfigService } from '@shared-lib/shared-services/config.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    importProvidersFrom(
      NgxEchartsModule.forRoot({ echarts: () => import('echarts') }),
    ),
    provideRouter(routes),
    provideAnimations(),
    // Config JSON is answered from the bundle first — no request leaves the app.
    // savedLocalhostUrlInterceptor runs before auth so the re-pointed request gets the token.
    provideHttpClient(withInterceptors([bundledConfigInterceptor, savedLocalhostUrlInterceptor, authHeadersInterceptor])),

    // Keycloak initializer
    {
      provide: APP_INITIALIZER,
      useFactory: (KeycloakInitService: KeycloakInitService) => () =>
        KeycloakInitService.initKeycloak(),
      multi: true,
      deps: [KeycloakInitService],
    },

    // Self-service API base URL initializer. JSON config keeps endpoint paths
    // relative; the host app supplies the environment-specific platform URL.
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {
        const baseUrl = selfServiceBaseUrl();
        if (baseUrl) {
          setSelfServiceApiOverrides({ baseUrl });
        }
      },
      multi: true,
    },

    // Platform API initializer
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: (platformService: PlatformService) => () => platformService.getDashboardConfigFromApi('oh', 'customer-overview').toPromise(),
    //   multi: true,
    //   deps: [PlatformService],
    // },

    KeycloakService,
    // PlatformService,

    // Custom ChartRendererService override (uses pink bar-chart, 70% radius pie-chart)
    { provide: SharedChartRendererService, useClass: ChartRendererService },
    { provide: SharedGlobalFilterService, useClass: CustomGlobalFilterService },
    {
      provide: SharedChartFilterHandlerService,
      useClass: CustomChartFilterHandlerService,
    },

    // Use CustomConfigService app-wide so all shared-lib components get the multi-row unwrapResponse fix
    { provide: SharedConfigService, useExisting: CustomConfigService },

    // Environment config for shared library components
    {
      provide: ENVIRONMENT_CONFIG,
      useValue: {
        COMPANY_LOGO_SRC: environment.COMPANY_LOGO_SRC,
        COMPANY_LOGO_TEXT: environment.COMPANY_LOGO_TEXT,
        HELP_URL: environment.HELP_URL,
        FEEDBACK_URL: environment.FEEDBACK_URL,
        SELF_SERVICE_API_BASE_URL: (environment as { SELF_SERVICE_API_BASE_URL?: string }).SELF_SERVICE_API_BASE_URL,
        USE_ALT_SIDENAV: environment.USE_ALT_SIDENAV,
        PROJECT_MODE_QA: environment.PROJECT_MODE_QA,
        PROJECT_MODE_UAT: environment.PROJECT_MODE_UAT,
      },
    },
  ],
};
